import * as React from 'react';
import moment from 'moment-timezone';
import { assertNever } from '@deltasierra/shared';

type RelativeTimestampFormat = 'basic' | 'detailed';

function getDateWithTimezone(
    dateOrString: Date | string,
    timezone?: string,
): { date: moment.Moment } & ({ withTimezone: false } | { withTimezone: true; timezone: string }) {
    const date = moment(dateOrString);
    const timezoneTrimmed = timezone?.trim();

    if (!timezoneTrimmed || timezoneTrimmed === '') {
        return {
            date,
            withTimezone: false,
        };
    }
    return {
        date: date.tz(timezoneTrimmed),
        timezone: timezoneTrimmed,
        withTimezone: true,
    };
}

function getDateString(dateOrString: Date | string, format: RelativeTimestampFormat, _timezone?: string): string {
    const { date } = getDateWithTimezone(dateOrString, _timezone);
    switch (format) {
        case 'basic':
            return date.fromNow();
        case 'detailed':
            return moment.duration(date.diff(moment())).humanize(true, {
                ss: 0,
            });
        default:
            return assertNever(format);
    }
}

export interface RelativeTimestampProps {
    autoUpdate?: boolean;
    autoUpdateIntervalMs?: number;
    date: Date | string;
    format?: RelativeTimestampFormat;
    timezone?: string;
}

export const RelativeTimestamp: React.FC<RelativeTimestampProps> = ({
    autoUpdate = false,
    autoUpdateIntervalMs = 1 * 1000,
    date,
    format = 'basic',
    timezone,
}) => {
    const [, updateUpdater] = React.useState(0);
    const dateString: string = getDateString(date, format, timezone);

    React.useEffect(() => {
        if (!autoUpdate) {
            return;
        }
        const intervalHandle = setInterval(() => updateUpdater(old => old + 1), autoUpdateIntervalMs);
        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(intervalHandle);
        };
    }, [autoUpdate, autoUpdateIntervalMs, updateUpdater]);

    return <>{dateString}</>;
};
RelativeTimestamp.displayName = 'RelativeTimestamp';
